import { fromBase64, toBase64 } from '../bytes/encoding';
import { ParseError } from '../bytes/errors';
import type { LogTransport } from '../transport/logTransport';
import { parseCertificate } from '../x509/parse';
import type { DiscoveredChain } from './types';

export const CRTSH_GEN_ADD_CHAIN = 'https://crt.sh/gen-add-chain';
/** Pseudo log id used for reachability bookkeeping of crt.sh requests. */
export const CRTSH_ID = 'crt.sh';

/**
 * Asks crt.sh to build an add-chain payload for a certificate or precertificate from its database of
 * CA certificates (https://crt.sh/gen-add-chain?b64cert=…). Works for anything crt.sh has seen the
 * issuers of — i.e. practically every publicly trusted CA — without touching AIA URLs.
 * crt.sh sends no CORS headers, so in a browser this goes through the configured proxy.
 */
export async function chainFromCrtsh(certDer: Uint8Array, transport: LogTransport, signal?: AbortSignal): Promise<DiscoveredChain> {
  // crt.sh never sends CORS headers: skip the doomed direct attempt (which would wait for the
  // full, often slow, response before failing) and go straight to the proxy / desktop fetcher.
  if (!transport.reachability.get(CRTSH_ID, 'issuer')) {
    transport.reachability.set(CRTSH_ID, 'issuer', { status: 'cors-blocked', at: Date.now(), error: 'crt.sh sends no Access-Control-Allow-Origin' });
  }
  const url = `${CRTSH_GEN_ADD_CHAIN}?b64cert=${encodeURIComponent(toBase64(certDer))}`;
  const { value } = await transport.json<{ chain?: unknown }>({ id: CRTSH_ID }, 'issuer', url, signal ? { signal } : undefined);
  if (!value || !Array.isArray(value.chain) || value.chain.length === 0 || !value.chain.every((c) => typeof c === 'string')) {
    throw new ParseError('crt.sh: response has no "chain" array');
  }
  const chain = (value.chain as string[]).map(fromBase64);
  // crt.sh echoes the submitted certificate as the first element; guard against surprises.
  if (chain[0]!.toHex() !== certDer.toHex()) chain.unshift(certDer);
  const parsed = chain.map((d) => parseCertificate(d));
  const last = parsed[parsed.length - 1]!;
  return { chain, subjects: parsed.map((p) => p.subject), endsAtRoot: last.subject === last.issuer, source: 'crt.sh', detail: 'crt.sh gen-add-chain' };
}
